import React from 'react';

import classNames from 'classnames';

import { ActionType } from '../../../../sharedModules/getActionType';
import { PromoType, getPromoItems } from '../../../../sharedModules/getPromoItems';
import { LegacyActionEvent } from '../../../../types/LegacyActionEvent';
import getUrlParameters from '../../../../utils/getUrlParameters';
import type { Deal } from '../../../types/Deal';
import { DealCountdown } from '../DealCountdown/DealCountdown';
import getDealTimes from '../DealCountdown/getDealTimes';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetContext';
import { Promos } from '../Promos/loadable';

import GridItemMain from './GridItemMain';
import GridItemToggle from './GridItemToggle';
import { GridItemsClassName } from './model';
import styles from './styles/grid-item.css';

interface GridItemProps {
  deal: Deal;
  className: GridItemsClassName;
  index?: number;
  children: (deal: Deal, index?: number) => JSX.Element;
  expandedChildren?: (deal: Deal, index?: number) => JSX.Element;
  stacked?: boolean;
  showPromos?: string;
  showSpacer?: boolean;
  showCountdown?: string;
  widgetName: string;
}

const GridItem: React.FC<GridItemProps> = (props) => {
  const {
    showPromos,
    className,
    index,
    children,
    showSpacer,
    expandedChildren,
    stacked,
    deal,
    widgetName,
  } = props;
  let { showCountdown } = props;
  const { sendAnalytics, dealData, activeTab, loadMore, translate, language, area } =
    useHawkWidgetContext();
  const [expanded, toggleExpandedState] = React.useState(false);

  const params = getUrlParameters(['end_time', 'start_time']);
  const dealEndTime = getDealTimes(deal, params, 'end_time');
  const dealStartTime = getDealTimes(deal, params, 'start_time');

  const moreDeals =
    dealData?.totalDeals > activeTab?.pageSize && loadMore
      ? dealData?.totalDeals > (dealData?.deals || []).length
      : false;

  if (children) {
    showCountdown = deal?.end_time ? showCountdown : '';

    const toggleExpanded = (): void => {
      if (deal) {
        sendAnalytics({
          affiliate: {
            action: {
              type: ActionType.ROW_TOGGLE,
              event: LegacyActionEvent.CLICK_FROM,
            },
            component: {
              label: expanded ? 'Toggle Closed' : 'Toggle Opened',
            },
          },
          deal,
          dealData,
        });
      }

      toggleExpandedState((prevValue) => !prevValue);
    };

    const getToggleExpanded = (expandedChildren): (() => void) | undefined => {
      if (expandedChildren) {
        return toggleExpanded;
      }

      return undefined;
    };

    const promoItems = getPromoItems(deal, PromoType.ALLOWED, false);

    const promosGrid = promoItems.length > 0 && (
      <GridItemMain deal={deal} className={className} stacked={stacked} type="promos">
        <>
          <Promos key="promos" items={promoItems} className={widgetName || 'promos'} />
        </>
      </GridItemMain>
    );
    const countdownGrid = (
      <GridItemMain deal={deal} className={className} stacked={stacked}>
        <DealCountdown
          dealStartTime={dealStartTime ?? undefined}
          dealEndTime={dealEndTime ?? undefined}
          translate={translate}
          language={language}
          area={area}
          className={widgetName}
        />
      </GridItemMain>
    );

    return (
      <div
        className={classNames(styles['container'], className.gridItem)}
        data-expandable={expandedChildren ? 1 : 0}
        data-stacked={stacked ? 1 : 0}
        data-load-more={moreDeals}
        onClick={getToggleExpanded(expandedChildren)}
        role="button"
        tabIndex={0}
        onKeyDown={getToggleExpanded(expandedChildren)}
      >
        {showPromos === 'above' && promosGrid}
        {showCountdown === 'above' && countdownGrid}
        <GridItemMain deal={deal} stacked={stacked} className={className}>
          {children(deal, index)}
        </GridItemMain>
        {showPromos === 'below' && promosGrid}
        {showCountdown === 'below' && countdownGrid}
        {expandedChildren && (
          <GridItemMain deal={deal} className={className} stacked={stacked}>
            <>
              <div
                key="toggle"
                onClick={toggleExpanded}
                role="button"
                tabIndex={0}
                onKeyDown={toggleExpanded}
              >
                <GridItemToggle expanded={expanded} toggleExpanded={toggleExpanded} />
              </div>
            </>
          </GridItemMain>
        )}
        {expanded && expandedChildren && (
          <div className={styles.expanded}>
            <GridItemMain deal={deal} stacked={stacked} expanded={true} className={className}>
              {expandedChildren(deal, index)}
            </GridItemMain>
          </div>
        )}
        {showSpacer && <div className={styles.spacer} />}
      </div>
    );
  }
  return null;
};

export default GridItem;
